/**
 * What the connectivity panel tells the operator (PLAN §10.2's failure-case
 * table), as one pure function of the connectivity state:
 *
 *   Browser→node      Browser→cloud  Node→cloud          case                message
 *   ✓                 ✓              ✓ (connected)       all-good            All systems connected
 *   ✓                 ✓              ✗ (disconnected)    site-lost-cloud     Studio has lost the cloud
 *   ✓                 ✗              ✗                   studio-offline      Studio offline
 *   ✗ (A) / ✓ (B)     any            any                 node-unreachable    <A> unreachable
 *   ✗                 ✓              any                 local-unreachable   Local nodes unreachable from here
 *
 * and the cases the table leaves implicit:
 *
 *   ✓                 ✗              ✓                   browser-lost-cloud  Cloud unreachable from this browser
 *   ✓                 any            not configured      link-not-configured the site link is not set up (before phase 4)
 *   ✓                 (no cloud)     —                   standalone          a node running without a cloud
 *   (no nodes)        ✓              —                   cloud-only          a cloud page with no local nodes
 *   ✗ everything                                         offline             No connection
 *   nothing known yet                                    checking            Checking connections…
 *
 * "Node→cloud" is what the nodes report about their own site link (`cloudLink`
 * in their `/health`). The tone is a broadcast token (D25): `success`,
 * `warning`, `standby` (informational), `destructive` (nothing reachable).
 */
import type { ConnectivityState, EndpointStatus, Reach } from './state.js';

export type ConnectivityCase =
  | 'checking'
  | 'all-good'
  | 'site-lost-cloud'
  | 'studio-offline'
  | 'browser-lost-cloud'
  | 'link-not-configured'
  | 'node-unreachable'
  | 'local-unreachable'
  | 'standalone'
  | 'cloud-only'
  | 'offline';

export type ConnectivityTone = 'success' | 'warning' | 'standby' | 'destructive';

export type ConnectivitySummary = {
  readonly case: ConnectivityCase;
  readonly tone: ConnectivityTone;
  /** A few words for the top bar. */
  readonly headline: string;
  /** One or two sentences: what it means for the production, and what is queued. */
  readonly detail: string;
  /** Edits cannot reach the studio from here (the cloud's view only). */
  readonly readOnly: boolean;
};

/** Patches waiting in the reachable nodes' outboxes, or null when no node reported. */
function outboxOf(nodes: readonly EndpointStatus[]): number | null {
  const reports = nodes.filter((n) => n.reach === 'up' && n.node !== null);
  return reports.length === 0 ? null : reports.reduce((sum, n) => sum + (n.node?.outboxDepth ?? 0), 0);
}

function queued(depth: number | null): string {
  if (depth === null || depth === 0) return 'Nothing is waiting to sync.';
  return depth === 1 ? '1 change is waiting in the studio’s outbox.' : `${String(depth)} changes are waiting in the studio’s outbox.`;
}

/** The nodes' own view of their site link: connected if any is, disconnected if all that report are, else not configured. */
function siteLink(up: readonly EndpointStatus[]): 'connected' | 'disconnected' | 'not-configured' {
  const links = up.flatMap((n) => (n.node === null ? [] : [n.node.cloudLink]));
  if (links.includes('connected')) return 'connected';
  if (links.includes('disconnected')) return 'disconnected';
  return 'not-configured';
}

function names(nodes: readonly EndpointStatus[]): string {
  return nodes.map((n) => n.endpoint.label).join(', ');
}

type Parts = { nodes: EndpointStatus[]; up: EndpointStatus[]; down: EndpointStatus[]; cloud: Reach | 'none' };

function partsOf(state: ConnectivityState): Parts {
  const nodes = state.endpoints.filter((s) => s.endpoint.kind === 'node');
  const cloud = state.endpoints.find((s) => s.endpoint.kind === 'cloud');
  return { nodes, up: nodes.filter((n) => n.reach === 'up'), down: nodes.filter((n) => n.reach === 'down'), cloud: cloud === undefined ? 'none' : cloud.reach };
}

const CHECKING: ConnectivitySummary = { case: 'checking', tone: 'standby', headline: 'Checking connections…', detail: 'Probing the studio and the cloud.', readOnly: false };

function withoutNodes(cloud: Reach | 'none'): ConnectivitySummary {
  if (cloud === 'up') return { case: 'cloud-only', tone: 'success', headline: 'Connected to the cloud', detail: 'No studio nodes are configured for this page.', readOnly: false };
  if (cloud === 'down') return { case: 'offline', tone: 'destructive', headline: 'No connection', detail: 'The cloud is unreachable from this browser. Check this computer’s network.', readOnly: true };
  return CHECKING;
}

function allNodesDown(p: Parts): ConnectivitySummary {
  const who = p.nodes.length === 1 ? names(p.nodes) : 'the studio nodes';
  if (p.cloud === 'up') {
    return {
      case: 'local-unreachable',
      tone: 'warning',
      headline: 'Local nodes unreachable from here',
      detail: `This browser cannot reach ${who}. Showing the cloud’s last-known status, read-only.`,
      readOnly: true,
    };
  }
  if (p.cloud === 'unknown') return CHECKING;
  const cloudToo = p.cloud === 'none' ? '' : ' or the cloud';
  return { case: 'offline', tone: 'destructive', headline: 'No connection', detail: `This browser cannot reach ${who}${cloudToo}. Check this computer’s network.`, readOnly: true };
}

function nodesUp(p: Parts): ConnectivitySummary {
  const depth = outboxOf(p.up);
  if (p.cloud === 'none') return { case: 'standalone', tone: 'success', headline: 'Studio node connected', detail: 'This node runs without a cloud connection configured.', readOnly: false };
  if (p.cloud === 'unknown') return CHECKING;
  const link = siteLink(p.up);
  if (link === 'not-configured') {
    const here = p.cloud === 'up' ? 'Node and cloud reachable' : 'Node connected, cloud unreachable';
    return { case: 'link-not-configured', tone: 'standby', headline: here, detail: 'The studio’s link to the cloud is not set up yet: edits here stay on the node.', readOnly: false };
  }
  if (p.cloud === 'up' && link === 'connected') return { case: 'all-good', tone: 'success', headline: 'All systems connected', detail: queued(depth), readOnly: false };
  if (p.cloud === 'up') {
    return {
      case: 'site-lost-cloud',
      tone: 'warning',
      headline: 'Studio has lost the cloud',
      detail: `This browser reaches both, so the studio’s uplink or firewall is the problem. Edits made in the cloud wait there; edits made here wait in the node. ${queued(depth)}`,
      readOnly: false,
    };
  }
  if (link === 'connected') {
    return { case: 'browser-lost-cloud', tone: 'standby', headline: 'Cloud unreachable from this browser', detail: 'The studio is still connected to the cloud. Nothing is lost.', readOnly: false };
  }
  return { case: 'studio-offline', tone: 'warning', headline: 'Studio offline', detail: `The production is unaffected; edits sync when the link returns. ${queued(depth)}`, readOnly: false };
}

export function describeConnectivity(state: ConnectivityState): ConnectivitySummary {
  const p = partsOf(state);
  if (p.nodes.length === 0) return withoutNodes(p.cloud);
  if (p.up.length === 0 && p.down.length === 0) return CHECKING;
  if (p.up.length === 0) return allNodesDown(p);
  if (p.down.length > 0) {
    const lost = names(p.down);
    return {
      case: 'node-unreachable',
      tone: 'warning',
      headline: `${lost} unreachable`,
      detail: `This browser cannot reach ${lost}. Working through ${names(p.up)}.`,
      readOnly: false,
    };
  }
  return nodesUp(p);
}
